import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { ApiError } from '../../lib/api';
import { mediaApi } from '../../lib/mediaApi';

/** Every uploaded icon is exported at this exact pixel size regardless of what
 * was uploaded — that's what lets the map use one fixed OL Icon scale and have
 * every layer's markers come out the same on-screen size (see
 * MapContext's ICON_MARKER_SCALE). */
const OUTPUT_SIZE = 128;
const PREVIEW_SIZE = 240;

/** A minimal circular crop/pan/zoom editor — pick an image, drag to position it,
 * slide to zoom, save uploads a fixed-size circular PNG via the shared media
 * API and hands back its file id. No cropper library in this project, and the
 * interaction is simple enough (pan + zoom within a circle) not to need one. */
export function CircleIconEditor({
  open,
  onClose,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  onUploaded: (fileId: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const draggingRef = useRef<{ x: number; y: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setImage(null);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setError(null);
    }
  }, [open]);

  const handleFile = (file: File) => {
    setError(null);
    const img = new Image();
    img.onload = () => {
      setImage(img);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    img.onerror = () => setError('Не удалось прочитать изображение.');
    img.src = URL.createObjectURL(file);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
    if (!image) return;
    ctx.save();
    ctx.beginPath();
    ctx.arc(PREVIEW_SIZE / 2, PREVIEW_SIZE / 2, PREVIEW_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = '#f1f5f9';
    ctx.fillRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
    const baseScale = Math.max(PREVIEW_SIZE / image.width, PREVIEW_SIZE / image.height);
    const scale = baseScale * zoom;
    const w = image.width * scale;
    const h = image.height * scale;
    ctx.drawImage(image, (PREVIEW_SIZE - w) / 2 + offset.x, (PREVIEW_SIZE - h) / 2 + offset.y, w, h);
    ctx.restore();
    ctx.beginPath();
    ctx.arc(PREVIEW_SIZE / 2, PREVIEW_SIZE / 2, PREVIEW_SIZE / 2 - 1, 0, Math.PI * 2);
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [image, zoom, offset]);

  const handlePointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    draggingRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
  };
  const handlePointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!draggingRef.current) return;
    setOffset({ x: e.clientX - draggingRef.current.x, y: e.clientY - draggingRef.current.y });
  };
  const handlePointerUp = () => {
    draggingRef.current = null;
  };

  const handleSave = async () => {
    if (!image) return;
    setUploading(true);
    setError(null);
    try {
      const out = document.createElement('canvas');
      out.width = OUTPUT_SIZE;
      out.height = OUTPUT_SIZE;
      const ctx = out.getContext('2d');
      if (!ctx) throw new Error('canvas unsupported');
      ctx.beginPath();
      ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
      ctx.clip();
      const ratio = OUTPUT_SIZE / PREVIEW_SIZE;
      const baseScale = Math.max(PREVIEW_SIZE / image.width, PREVIEW_SIZE / image.height);
      const scale = baseScale * zoom * ratio;
      const w = image.width * scale;
      const h = image.height * scale;
      ctx.drawImage(image, (OUTPUT_SIZE - w) / 2 + offset.x * ratio, (OUTPUT_SIZE - h) / 2 + offset.y * ratio, w, h);

      const blob = await new Promise<Blob>((resolve, reject) =>
        out.toBlob((b) => (b ? resolve(b) : reject(new Error('export failed'))), 'image/png'),
      );
      const uploaded = await mediaApi.upload(new File([blob], 'icon.png', { type: 'image/png' }));
      onUploaded(uploaded.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить иконку.');
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-[min(380px,100%)] rounded-2xl bg-bg-surface p-6 shadow-xl">
        <h2 className="text-base font-semibold text-text-primary">Иконка точки</h2>
        <p className="mt-1 text-xs text-text-secondary">
          Загрузите изображение и разместите его в круге — на карте маркер всегда одного размера, независимо от исходного
          изображения.
        </p>

        {!image ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="mt-4 flex h-40 w-full items-center justify-center rounded-xl border-2 border-dashed border-border-default text-xs font-medium text-text-secondary hover:bg-bg-surface-secondary"
          >
            Выбрать изображение
          </button>
        ) : (
          <>
            <div className="mt-4 flex justify-center">
              <canvas
                ref={canvasRef}
                width={PREVIEW_SIZE}
                height={PREVIEW_SIZE}
                className="cursor-move touch-none rounded-full bg-bg-surface-secondary"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
              />
            </div>
            <label className="mt-3 block text-[11px] font-medium text-text-secondary">Масштаб</label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="mt-1 w-full accent-action-primary"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="mt-2 text-[11px] font-medium text-action-primary hover:underline"
            >
              Выбрать другое изображение
            </button>
          </>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) handleFile(file);
          }}
        />

        {error && <p className="mt-3 text-xs font-medium text-status-danger">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!image || uploading}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-4 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {uploading ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}
